import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImagePlus, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LANGS = ['it', 'en', 'es', 'de'];

const SLOTS = [
  { key: 'home-hero', label: 'Home — foto principale in alto' },
  { key: 'home-ch1', label: 'Home — capitolo 01 "Chi siamo"' },
  { key: 'home-ch2', label: 'Home — capitolo 02 "Ovunque voi siate"' },
  { key: 'home-ch3', label: 'Home — capitolo 03 "La location"' },
  { key: 'serv-0', label: 'Servizi — Cooking Team Building' },
  { key: 'serv-1', label: 'Servizi — Wine & Beer Experience' },
  { key: 'serv-2', label: 'Servizi — Cocktail & Food Art' },
  { key: 'serv-3', label: 'Servizi — Eventi Aziendali' },
  { key: 'serv-4', label: 'Servizi — Eventi di Promozione' },
  { key: 'serv-5', label: 'Servizi — Catering & Banqueting' },
  { key: 'serv-6', label: 'Servizi — Affitto Location' },
  { key: 'loc-hero', label: 'Location — foto in alto' },
  { key: 'loc-floor0', label: 'Location — piano 0 (cucina)' },
  { key: 'loc-floor1', label: 'Location — piano -1 (cantina)' },
  { key: 'about-photo', label: 'Chi Siamo — ritratto' },
];

const DEFAULTS = {
  'home-hero': '/images/foto-progetto.jpg',
  'home-ch1': '/images/chi-siamo.jpg',
  'home-ch2': '/images/burrata-cup.jpg',
  'home-ch3': '/images/location-nuova.jpg',
  'serv-0': '/images/cooking-nuova.jpg',
  'serv-1': '/images/wine-beer.jpg',
  'serv-2': '/images/mixology.jpg',
  'serv-3': '/images/piano-terra.jpg',
  'serv-4': '/images/promozione.jpg',
  'serv-5': '/images/catering.jpg',
  'serv-6': '/images/location-nuova.jpg',
  'loc-hero': '/images/location-evento.jpg',
  'loc-floor0': '/images/foto-progetto.jpg',
  'loc-floor1': '/images/cantina.jpg',
  'about-photo': '/images/emma.jpg',
};

const SERVICE_NAMES = ['Cooking Team Building', 'Wine & Beer Experience', 'Cocktail & Food Art', 'Eventi Aziendali', 'Eventi di Promozione', 'Catering & Banqueting', 'Affitto Location'];

const TEXT_FIELDS = [
  { key: 'hero_line1', label: 'Home — titolo, riga 1' },
  { key: 'hero_line2', label: 'Home — titolo, riga 2' },
  { key: 'hero_line3', label: 'Home — titolo, riga 3' },
  { key: 'hero_sub', label: 'Home — sottotitolo' },
  { key: 'about_bio', label: 'Chi Siamo — biografia', rows: 8 },
  ...SERVICE_NAMES.flatMap((n, i) => [
    { key: `serv_title_${i}`, label: `Servizi — titolo: ${n}` },
    { key: `serv_desc_${i}`, label: `Servizi — descrizione: ${n}`, rows: 3 },
  ]),
];

export default function AdminContent({ token }) {
  const [contents, setContents] = useState({});
  const [manifest, setManifest] = useState({});
  const [lang, setLang] = useState('it');
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState('');
  const fileRefs = useRef({});

  useEffect(() => {
    fetch(`${API}/api/contents`).then((r) => r.json()).then(setContents).catch(() => {});
    fetch(`${API}/api/assets-manifest`).then((r) => r.json()).then(setManifest).catch(() => {});
  }, []);

  const upload = async (slot, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/admin/assets/${slot}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      setManifest((m) => ({ ...m, [slot]: Date.now() }));
      toast.success('Foto aggiornata: è già visibile sul sito');
    } catch {
      toast.error('Caricamento non riuscito (max 8 MB, JPG/PNG/WebP)');
    }
  };

  const saveText = async (key) => {
    const fullKey = `${lang}.${key}`;
    const value = values[fullKey] ?? contents[fullKey] ?? '';
    setSaving(fullKey);
    try {
      const res = await fetch(`${API}/api/admin/contents`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: fullKey, value }),
      });
      if (!res.ok) throw new Error();
      setContents((c) => ({ ...c, [fullKey]: value }));
      toast.success('Testo salvato: è già visibile sul sito');
    } catch {
      toast.error('Salvataggio non riuscito');
    } finally {
      setSaving('');
    }
  };

  return (
    <div data-testid="admin-content-panel" className="space-y-16">
      <section>
        <h2 className="font-serif text-3xl text-emerald">Foto delle pagine</h2>
        <p className="mt-2 text-sm text-emerald-ink/60">Carica una nuova foto: sostituisce subito quella attuale sul sito. Formati: JPG, PNG o WebP, max 8 MB.</p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SLOTS.map((s) => (
            <div key={s.key} className="bg-white border border-emerald/10 rounded-sm overflow-hidden" data-testid={`slot-${s.key}`}>
              <img
                src={manifest[s.key] ? `${API}/api/assets/${s.key}?v=${manifest[s.key]}` : DEFAULTS[s.key]}
                alt={s.label}
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="p-4 flex items-center justify-between gap-3">
                <p className="text-xs text-emerald-ink/70 leading-snug">{s.label}</p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  ref={(el) => (fileRefs.current[s.key] = el)}
                  onChange={(e) => { upload(s.key, e.target.files[0]); e.target.value = ''; }}
                  data-testid={`photo-input-${s.key}`}
                />
                <button
                  onClick={() => fileRefs.current[s.key]?.click()}
                  data-testid={`photo-upload-${s.key}`}
                  className="shrink-0 inline-flex items-center gap-2 rounded-full bg-emerald text-ivory px-4 py-2 text-xs font-medium transition-colors duration-300 hover:bg-terracotta-dark"
                >
                  <ImagePlus size={14} /> Cambia
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-3xl text-emerald">Testi principali</h2>
        <p className="mt-2 text-sm text-emerald-ink/60">Scegli la lingua, modifica il testo e salva. Lascia vuoto per usare il testo attuale del sito.</p>
        <div className="mt-6 flex gap-2" data-testid="content-lang-tabs">
          {LANGS.map((l) => (
            <button
              key={l}
              data-testid={`content-lang-${l}`}
              onClick={() => setLang(l)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors duration-300 ${
                lang === l ? 'bg-emerald text-ivory' : 'bg-white border border-emerald/15 text-emerald hover:border-terracotta'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="mt-8 space-y-6">
          {TEXT_FIELDS.map((f) => {
            const fullKey = `${lang}.${f.key}`;
            return (
              <div key={fullKey} className="bg-white border border-emerald/10 rounded-sm p-5" data-testid={`field-${f.key}`}>
                <label className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">{f.label} <span className="text-terracotta-dark">({lang.toUpperCase()})</span></label>
                <textarea
                  rows={f.rows || 2}
                  className="w-full bg-ivory-soft border border-emerald/15 rounded-sm px-4 py-3 text-sm text-emerald-ink outline-none transition-colors duration-300 focus:border-terracotta resize-y"
                  value={values[fullKey] ?? contents[fullKey] ?? ''}
                  placeholder="Lascia vuoto per usare il testo attuale del sito"
                  onChange={(e) => setValues((v) => ({ ...v, [fullKey]: e.target.value }))}
                  data-testid={`text-${f.key}`}
                />
                <button
                  onClick={() => saveText(f.key)}
                  disabled={saving === fullKey}
                  data-testid={`save-${f.key}`}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald text-ivory px-5 py-2 text-xs font-medium transition-colors duration-300 hover:bg-terracotta-dark disabled:opacity-60"
                >
                  <Save size={13} /> {saving === fullKey ? 'Salvataggio…' : 'Salva'}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
